/**
 *
 *  PasswordConfirmForm
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback, MouseEvent, ChangeEvent } from 'react';
import { ScrollView } from 'react-native';
import clsx from 'clsx';
import preval from 'preval.macro';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import { useAppState } from '../../state';
import { useSignUpState } from '../../state/SignUpState';

import { Theme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// Components
import PhoneCodeInput from '../PhoneCodeInput/PhoneCodeInput';
import PasswordInput from '../PasswordInput/PasswordInput';

// Hooks
import useCountryName from '../../hooks/useCountryName/useCountryName';

// Icons
import CheckNiceIcon from '../../icons/CheckNiceIcon';
import ArrowRightIcon from '../../icons/ArrowRightIcon';

// Utils
import { getRegionFromUserLanguages } from '../../utils/locale';
import { formatPhoneNumber } from '../../utils';

import Partner from '../LoginPage/partner';
import TruBeLogo from '../LoginPage/TruBeLogo';

import { makeStyles } from '@material-ui/core/styles';
// import { useLocation, useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  // googleButton: {
  //   background: 'white',
  //   color: 'rgb(0, 94, 166)',
  //   'border-radius': '4px',
  //   border: '2px solid rgb(2, 122, 197)',
  //   margin: '1.8em 0 0.7em',
  //   'text-transform': 'none',
  //   'box-shadow': 'none',
  //   padding: '0.3em 1em',
  //   [theme.breakpoints.down('sm')]: {
  //     width: '100%',
  //   },
  //   '&:hover': {
  //     background: 'white',
  //     'box-shadow': 'none',
  //   },
  // },
  errorMessage: {
    color: 'red',
    display: 'flex',
    'align-items': 'center',
    margin: '-8px 0 24px',
    '& svg': {
      'margin-right': '0.4em',
    },
  },
  // container: {
  //   'margin-top': '80px', //'2.5em',
  //   padding: '24px 16px',
  //   // height: '100%',
  //   // 'margin-bottom': '2.5em',
  // },
  partnerLabelContainer: {
    position: 'relative',
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    'margin-bottom': '45px',
    'background-image': Partner,
    'background-size': '100%',
    'background-repeat': 'no-repeat',
    width: '107px',
    height: '36px',
    [theme.breakpoints.down('sm')]: {
      // width: '100%',
      // height: '100px',
      // backgroundPositionY: '140px',
    },
  },
  trubeLogo: {
    // position: 'absolute',
    // top: 0,
    // left: '0',
    'margin-bottom': '10px',
  },
  gutterBottom: {
    'margin-bottom': '1em',
  },
  gutterBottomLarge: {
    'margin-bottom': '1.5em',
  },
  greyColor: {
    color: theme.grey,
  },
  // passwordContainer: {
  //   // 'min-height': '120px',
  //   'margin-bottom': '24px',
  // },
  phoneCodeInput: {
    'margin-right': '8px',
  },
  submitButton: {
    // className="tb-button tbk-mb-2 tbk-w-full"
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  linkLikeButton: {
    'text-decoration': 'underline',
  },

  contactSupportButton: {
    cursor: 'pointer',
    display: 'flex',
    background: 'transparent',
    border: '0',
    padding: '0',
  },
}));

const defaultRegion = getRegionFromUserLanguages();

export default function PasswordConfirmForm() {
  const classes = useStyles();
  const history = useHistory();

  const {
    user,
    sendResetCode,
    resetPassword,
    isPasswordResetReady,
    // resetCode,
    error,
    isFetching,
  } = useAppState();
  const { setAuthState, setUserPhoneCode, userPhoneCode, setUserPhoneNumber, userPhoneNumber } = useSignUpState();

  const [canSend, setCanSend] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [phoneCode, setPhoneCode] = useState(defaultRegion === 'GB' ? '+44' : '+1');
  const [phoneNumber, setPhoneNumber] = useState<string>(userPhoneNumber);
  const [countryFlag, setCountryFlag] = useState<string>(defaultRegion === 'GB' ? '🇬🇧' : '🇺🇸');
  const [latlng, setLatlng] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);

  const newPasswordRef = React.useRef<HTMLInputElement>(null);
  const newPasswordConfirmRef = React.useRef<HTMLInputElement>(null);
  const phoneNumberRef = React.useRef<HTMLInputElement>(null);
  const phoneCodeInputRef = React.useRef<HTMLInputElement>(null);

  const [, countryShortName, lookupAddress] = useCountryName(latlng);

  useEffect(() => {
    let latLng: string;

    fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${process.env.REACT_APP_IPGEOLOCATION_API_KEY}`)
      .then(response => response.json())
      .then(data => {
        const countryCode = data.country_code2;

        if (countryCode === 'GB') {
          setCountryFlag('🇬🇧');
          setPhoneCode('+44');
          setUserPhoneCode('+44');
        } else {
          setCountryFlag('🇺🇸');
          setPhoneCode('+1');
          setUserPhoneCode('+1');
        }
      })
      .catch(res => {
        Sentry.captureMessage(`ipgeolocation failed! #: ${JSON.stringify(res)}`);

        // FIXME refactor this
        if (navigator.geolocation /* && !userPhoneCode*/) {
          navigator.geolocation.getCurrentPosition(
            pos => {
              latLng = `${pos.coords.latitude},${pos.coords.longitude}`;
              setLatlng(latLng);

              // @ts-ignore
              lookupAddress(latLng);
            },
            err => {
              console.warn(`ERROR(${err.code}): ${err.message}`);
              setLatlng(`${34.098907},${-118.327759}`);

              // lookupAddress(latlng);
            }
          );
        }
      });
  }, [lookupAddress]);

  useEffect(() => {
    if (countryShortName) {
      // let [phoneCode, countryFlag, countryFlagImage] =
      //   getCountryInfo(countryShortName);

      if (countryShortName === 'GB') {
        setCountryFlag('🇬🇧');
        setPhoneCode('+44');
        setPhoneCode('+44');
      } else {
        setCountryFlag('🇺🇸');
        setPhoneCode('+1');
        setPhoneCode('+1');
      }
    }
  }, [countryShortName]);

  const handlePasswordChange = useCallback(e => {
    const value = e.target.value;

    setHasError(false);
    setPassword(value);
  }, []);

  const handlePasswordConfirmChange = useCallback(e => {
    const value = e.target.value;

    setHasError(false);
    setPasswordConfirm(value);
  }, []);

  useEffect(() => {
    if (!isPasswordResetReady && phoneCode && phoneNumber) {
      // sendResetCode(phoneCode, phoneNumber).then(() => {
      //   setAuthState("passwordResetCodeVerification");
      // });
    }
  }, [
    // eslint-disable-line react-hooks/exhaustive-deps
    phoneCode,
    phoneNumber,
    isPasswordResetReady,
  ]);

  useEffect(() => {
    if (isPasswordResetReady) {
      if (password && passwordConfirm && password === passwordConfirm) {
        setCanSend(true);
      } else {
        setCanSend(false);
      }
    } else {
      if (phoneNumber) {
        setCanSend(true);
      } else {
        setCanSend(false);
      }
    }
  }, [password, passwordConfirm, phoneNumber]);

  const handlePhoneCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setPhoneCode(value);
    setUserPhoneCode(value);

    if (value === '+44') {
      setCountryFlag('🇬🇧');
    } else if (value === '+1') {
      setCountryFlag('🇺🇸');
    }
  };

  const handlePhoneNumberChange = useCallback(e => {
    let value = e.target.value;

    value = formatPhoneNumber(value);

    setPhoneNumber(value);
    setUserPhoneNumber(value);

    setHasError(false);

    if (validatePhoneNumber(value)) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }
  }, []);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      if (isPasswordResetReady && password.length < 5) {
        setHasError(true);

        return;
      }

      if (isPasswordResetReady) {
        Sentry.captureMessage(`resetPassword request with #: ${phoneCode} ${phoneNumber}`);
        resetPassword(phoneCode, phoneNumber, password)
          .then(() => {
            // setAuthState('login');
            history.replace({ pathname: '/login' });
          })
          .catch(e => {
            Sentry.captureMessage(`resetPassword failed! (${e})`);
            setHasError(true);
          });
      } else if (/*!isPasswordResetReady && */ phoneCode && phoneNumber) {
        Sentry.captureMessage(`sendResetCode request with #: ${phoneCode} ${phoneNumber}`);
        sendResetCode(phoneCode, phoneNumber)
          .then(() => {
            setAuthState('passwordResetCodeVerification');
          })
          .catch(e => {
            Sentry.captureMessage(`sendResetCode failed! (${e})`);
            setHasError(true);
          });
      }
    },
    [phoneNumber, phoneCode, password, setAuthState, resetPassword, history]
  );

  return (
    <>
      {!isPasswordResetReady ? (
        <div
          // display="flex"
          // alignItems="center"
          // justifyContent="center"
          // flexDirection="column"
          // textAlign="center"
          // flexGrow={1}
          // // mb={5}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            // textAlign: 'center',
            flexGrow: 1,
          }}
        >
          <TruBeLogo className={classes.trubeLogo} />
          <div className={classes.partnerLabelContainer} />
          {/*<img src={Partner} />*/}
          {/*process.env.REACT_APP_SET_AUTH === 'firebase' && (
        <>
          <Typography variant="h5" className={classes.gutterBottom}>
            Sign in to join a room
          </Typography>
          <Typography variant="body1">Sign in using your Twilio Google Account</Typography>
          <Button variant="contained" className={classes.googleButton} onClick={login} startIcon={<GoogleLogo />}>
            Sign in with Google
          </Button>
        </>
      )*/}

          {/*<Typography variant="h5" className={classes.gutterBottom}>
        Enter password to join a room
      </Typography>*/}
          <Typography
            variant="subtitle2"
            // className={classes.gutterBottom}
            style={{
              alignSelf: 'start',
              // marginBottom: '40px',
              marginBottom: '45px',
            }}
          >
            Enter your phone number
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: '100%', marginBottom: '40px' }}>
            <Grid container justifyContent="space-between" wrap={'nowrap'} className={classes.gutterBottomLarge}>
              {/*<div  style={{ flexGrow: 1 }}>*/}
              {/*<InputLabel shrink htmlFor="input-email">
              Email
            </InputLabel>*/}
              <PhoneCodeInput
                ref={phoneCodeInputRef}
                className={classes.phoneCodeInput}
                // style={{ marginRight: "8px" }}
                countryFlag={countryFlag}
                countryShortName={countryShortName}
                //countryShortName="US" //{'US'}
                phoneCode={phoneCode}
                onChange={handlePhoneCodeChange}
                // onClick={() =>
                //   showChooseCodePopup ? null : setShowChooseCodePopup(true)
                // }
              />
              <TextField
                ref={phoneNumberRef}
                id="input-email"
                // onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                type="tel"
                variant="outlined"
                autoComplete="username"
                placeholder="Phone number"
                // size="small"
                style={{ width: '100%' }}
                inputProps={{
                  style: {
                    padding: '18px 16px 18px 16px',
                    // width: "50px",
                    // height: "64px",
                    // fontSize: '22px',
                  },
                }}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onKeyUp={handlePhoneNumberChange}
              />
            </Grid>
            {hasError && (
              <Typography variant="caption" className={classes.errorMessage}>
                <ErrorOutlineIcon />
                {error ? error.message : 'Unknown error has occurred'}
              </Typography>
            )}
            <Grid container justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                // disabled={!password.length}
                disabled={!canSend || isFetching}
                className={classes.submitButton}
                // className="tb-button tbk-mb-2 tbk-w-full"
                endIcon={canSend ? null : <ArrowRightIcon />}
              >
                Reset Password
              </Button>
            </Grid>
          </form>
          <a
            href={user && user.region === 'GB' ? 'https://trubeapp.com/contact' : 'https://us.trubeapp.com/contact'}
            className={classes.contactSupportButton}
            target="_blank"
            // onClick={() => history.replace({ pathname: '/password-reset' })}
          >
            <Typography
              variant="subtitle2"
              className={clsx(classes.linkLikeButton, classes.gutterBottom, classes.greyColor)}
            >
              Contact Support
            </Typography>
          </a>
        </div>
      ) : (
        <div
          // display="flex"
          // alignItems="center"
          // justifyContent="center"
          // flexDirection="column"
          // textAlign="center"
          // flexGrow={1}
          // // mb={5}
          style={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center',
            flexDirection: 'column',
            // textAlign: 'center',
            flexGrow: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            // className={classes.gutterBottom}
            style={{ marginBottom: '24px' }}
          >
            Set Your New Password.
          </Typography>

          {/*<Typography variant="subtitle2" style={{ marginBottom: '24px' }}>
            Please use uppercase and lowercase letters, numbers and symbols.
          </Typography>*/}
          <form onSubmit={handleSubmit} style={{ width: '100%', marginBottom: '40px' }}>
            <Grid container justifyContent="space-between">
              <div className={classes.gutterBottom} style={{ flexGrow: 1 }}>
                {/*<div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">*/}
                <Typography variant="subtitle1" style={{ marginBottom: '4px' }}>
                  New password:
                </Typography>
                <PasswordInput
                  ref={newPasswordRef}
                  autoComplete="new-password"
                  placeholder="Your password"
                  minLength={5}
                  maxLength={30}
                  pattern="\s*(\S\s*){5,30}"
                  password={password}
                  style={{ width: '100%', marginBottom: '16px' }}
                  onChange={handlePasswordChange}
                  // onInput={handlePasswordChange}
                />

                <Typography variant="subtitle1" style={{ marginBottom: '4px' }}>
                  Confirm new password:
                </Typography>
                <PasswordInput
                  ref={newPasswordConfirmRef}
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  minLength={5}
                  maxLength={30}
                  pattern="\s*(\S\s*){5,30}"
                  password={passwordConfirm}
                  style={{ width: '100%', marginBottom: '24px' }}
                  onChange={handlePasswordConfirmChange}
                  // onInput={handlePasswordConfirmChange}
                />
              </div>
              {hasError && (
                <Typography variant="caption" className={classes.errorMessage}>
                  <ErrorOutlineIcon />
                  {error ? error.message : 'Unknown error has occurred'}
                </Typography>
              )}
              {/*{hasError ? (
              <div className="tbk-text-main tbk--mt-2 tbk-mb-2 tbk-text-center tbk-text-coral">

              </div>
            ) : null}*/}
            </Grid>
            <Grid container justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                // disabled={!password.length}
                disabled={!canSend || isFetching}
                className={classes.submitButton}
                // showLoader={isFetching}
                endIcon={<CheckNiceIcon />}
              >
                Save new password
              </Button>
            </Grid>
          </form>
        </div>
      )}
    </>
  );
}

function validatePhoneNumber(phone: string): boolean {
  return phone ? phone.replace(/\s/g, '').length >= 9 : false;
}
